<template>
    <div>
        <v-btn v-if="canManageNc && !openOnMount" :color="buttonColor" small :disabled="disabled" @click="dialog = true">
            <template v-if="buttonLabel">{{ buttonLabel }}</template>
            <v-icon v-else>mdi-square-edit-outline</v-icon>
        </v-btn>
        <v-dialog
            v-model="dialog"
            :width="!$vuetify.breakpoint.mobile ? '88vw' : ''"
            max-width="1180"
            :fullscreen="$vuetify.breakpoint.mobile"
        >
            <v-card v-if="nc" class="mc-root mc-dialog nc-mecom nc-dialog-card">
                <v-progress-linear v-if="detailLoading || originLoading" indeterminate class="nc-progress"></v-progress-linear>
                <div class="nc-header">
                    <div class="nc-header-main">
                        <h2 class="t-title-2">{{ internalCaseId ? 'NC interna' : 'Dichiarazione' }} <span class="mc-code">{{ nc.numero }}</span><template v-if="!internalCaseId"> / riga {{ nc.riga }}</template></h2>
                        <div class="nc-state-group" aria-label="Stato della dichiarazione">
                            <mc-status-chip :tone="risolta ? 'good' : 'warn'" :icon="risolta ? 'check' : 'clock'" :label="materialState" />
                            <mc-status-chip :tone="canGenerate8d ? 'good' : declarationState === 'In compilazione' ? 'warn' : 'neutral'" :icon="canGenerate8d ? 'check' : 'clock'" :label="declarationState" />
                            <mc-status-chip :tone="canGenerate8d ? 'good' : 'neutral'" :icon="canGenerate8d ? 'check' : 'minus'" :label="canGenerate8d ? '8D disponibile' : '8D non disponibile'" />
                        </div>
                    </div>
                    <div class="nc-state-group nc-checklist" aria-label="Completezza della dichiarazione">
                        <mc-status-chip
                            v-for="item in decisionChecklist"
                            :key="item.key"
                            :tone="item.complete ? 'good' : 'neutral'"
                            :icon="item.complete ? 'check' : 'minus'"
                            :label="item.label"
                        />
                    </div>
                </div>

                <v-card-text class="nc-body">
                    <v-alert v-if="generationErrorList.length" type="warning" text dense class="mb-4">
                        <div class="font-weight-bold">
                            Il rapporto 8D non può essere generato perché la dichiarazione è incompleta.
                        </div>
                        <ul class="mt-1">
                            <li v-for="error in generationErrorList" :key="error.key || error">
                                {{ error.label || error }}
                            </li>
                        </ul>
                    </v-alert>

                    <v-alert v-if="Number(nc.numero_righe) > 1" type="info" text dense>
                        Questa NC contiene {{ nc.numero_righe }} righe.
                        <v-btn small text color="primary" :loading="copying" @click="copyConfirmDialog = true">
                            Copia i testi sulle altre righe incomplete
                        </v-btn>
                    </v-alert>

                    <v-form ref="ncform">
                        <v-card outlined class="nc-section mc-card">
                            <v-card-title class="t-title-3">1. Dati della non conformità</v-card-title>
                            <v-card-text>
                                <v-row dense>
                                    <v-col v-for="field in ncDataFields" :key="field.label" cols="12" sm="6" md="4">
                                        <span class="mc-caption">{{ field.label }}</span>
                                        <div class="font-weight-medium">{{ field.value || "—" }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <v-card outlined class="nc-section mc-card">
                            <v-card-title class="t-title-3">2. Origine del problema</v-card-title>
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-autocomplete
                                            outlined dense hide-details="auto"
                                            ref="odp"
                                            v-model="selectedOdp"
                                            label="ODP collegato alla non conformità"
                                            :items="odpOptions"
                                            item-text="label"
                                            item-value="numero"
                                            item-disabled="disabled"
                                            :loading="originLoading"
                                            :error-messages="fieldErrors('odp')"
                                            :clearable="!internalCaseId"
                                            :readonly="Boolean(internalCaseId)"
                                            :no-data-text="internalCaseId ? 'ODP collegato allo scarto' : 'Nessun ODP dello stesso articolo versato entro la data della NC'"
                                            @change="onOdpChange"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-autocomplete
                                            outlined dense hide-details="auto"
                                            ref="phase"
                                            v-model="selectedOriginChoice"
                                            label="Fase o origine del difetto"
                                            :items="originChoiceOptions"
                                            item-text="label"
                                            item-value="value"
                                            item-disabled="disabled"
                                            :loading="originLoading"
                                            :error-messages="fieldErrors('phase')"
                                            :disabled="!selectedOdp"
                                            clearable
                                            no-data-text="Nessuna fase o origine disponibile"
                                            @change="onOriginChoiceChange"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col v-if="nc.nc_origine_id === 'O17'" cols="12" md="6">
                                        <v-autocomplete
                                            outlined dense hide-details="auto"
                                            ref="supplier"
                                            v-model="selectedSupplier"
                                            label="Fornitore / terzista origine NC *"
                                            :items="supplierOptions"
                                            :item-text="supplierLabel"
                                            item-value="codice"
                                            :loading="originLoading"
                                            :error-messages="fieldErrors('supplier')"
                                            clearable
                                            no-data-text="Nessun fornitore collegato all’ODP e alla fase selezionati"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field :value="calculatedOriginLabel" label="Origine automatica" outlined dense hide-details="auto" readonly></v-text-field>
                                    </v-col>
                                </v-row>
                                <p class="nc-guide text--secondary t-caption">
                                    Le fasi appartengono all’ODP selezionato; l’origine viene calcolata automaticamente.
                                </p>
                                <v-autocomplete
                                            outlined dense hide-details="auto"
                                    v-if="responsibleErrorRequired"
                                    ref="operator"
                                    v-model="selectedOperator"
                                    label="Responsabile dell’errore"
                                    :items="responsibleOptions"
                                    :item-text="operatorLabel"
                                    :item-value="operatorValue"
                                    :filter="operatorFilter"
                                    :rules="operatorRules"
                                    :loading="originLoading"
                                    required
                                    clearable
                                    :hint="responsibleErrorHint"
                                    persistent-hint
                                    :no-data-text="responsibleErrorNoDataText"
                                >
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ operatorLabel(item) }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ operatorSubtitle(item) }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-autocomplete>
                            </v-card-text>
                        </v-card>

                        <v-card outlined class="nc-section mc-card">
                            <v-card-title class="t-title-3">3. Analisi e gestione</v-card-title>
                            <v-card-text>
                                <nc-guided-textarea
                                    ref="problem"
                                    label="Problema / requisito non rispettato"
                                    v-model="nc.problema"
                                    guide="Descrivere cosa non è conforme, dove si trova il difetto e quale requisito non è rispettato. Non indicare la causa o la soluzione."
                                    example=""
                                    :suggestions="problemSuggestions"
                                    :error-messages="fieldErrors('problem')"
                                ></nc-guided-textarea>
                                <nc-guided-textarea
                                    ref="correction"
                                    label="Correzione immediata / contenimento"
                                    v-model="nc.correzione_immediata"
                                    guide="Il contenimento è precompilato dalla gestione del reso e resta modificabile. È possibile aggiungere più azioni senza duplicati."
                                    example=""
                                    suggestion-mode="merge"
                                    :suggestions="correctionSuggestions"
                                    :error-messages="fieldErrors('correction')"
                                ></nc-guided-textarea>
                                <v-select
                                            outlined dense hide-details="auto"
                                    ref="cause"
                                    v-model="nc.nc_causa_id"
                                    label="Causa individuata"
                                    :items="filteredCauseOptions"
                                    item-text="label"
                                    item-value="id"
                                    :error-messages="fieldErrors('cause')"
                                ></v-select>
                                <p class="nc-guide text--secondary t-caption">
                                    Selezionare la condizione concreta che ha permesso al problema di verificarsi. Il sistema assegnerà automaticamente la categoria e compilerà la causa radice dell’8D.
                                </p>
                                <v-textarea
                                    ref="permanent_action"
                                    :value="nonDetectionCause"
                                    label="Causa della mancata intercettazione"
                                    outlined
                                    rows="2" dense auto-grow hide-details="auto"
                                    readonly
                                ></v-textarea>
                                <v-checkbox
                                    dense hide-details color="primary"
                                    v-model="nc.azione_non_necessaria"
                                    label="Azione correttiva permanente non necessaria"
                                ></v-checkbox>
                                <v-textarea
                                    v-if="nc.azione_non_necessaria"
                                    v-model="nc.motivazione_azione_non_necessaria"
                                    outlined
                                    rows="2" dense auto-grow hide-details="auto"
                                    label="Motivazione"
                                    hint="Minimo 10 caratteri; non usare nessuna, n/a, non serve, ok o fatto"
                                    persistent-hint
                                    :error-messages="fieldErrors('permanent_action')"
                                ></v-textarea>
                                <nc-guided-textarea
                                    v-else
                                    ref="permanent_action"
                                    label="Azione correttiva permanente"
                                    v-model="nc.azione_correttiva_permanente"
                                    guide="Selezionare o descrivere azioni definitive e verificabili, coerenti con origine e causa."
                                    example=""
                                    :suggestions="permanentSuggestions"
                                    :error-messages="fieldErrors('permanent_action')"
                                ></nc-guided-textarea>
                            </v-card-text>
                        </v-card>

                        <v-card outlined class="nc-section mc-card">
                            <v-card-title class="t-title-3">4. Chiusura e documenti</v-card-title>
                            <v-card-text>
                                <v-checkbox
                                    dense hide-details color="primary"
                                    v-model="risolta"
                                    label="Materiale della NC gestito"
                                    :loading="materialLoading"
                                    @change="updateMaterial"
                                ></v-checkbox>
                                <p class="nc-guide text--secondary t-caption">
                                    Il flag resta manuale e non blocca la disponibilità del rapporto 8D.
                                </p>
                                <template v-if="!internalCaseId"><v-row dense>
                                    <v-col v-for="(photo, photoId) in nc.foto" :key="photoId" cols="6" sm="3">
                                        <div class="nc-photo-preview">
                                            <v-img
                                                :src="thumburl + photo.code + '/'"
                                                width="100%"
                                                height="120"
                                                class="thumb"
                                                @click="openPhoto(photo)"
                                            ></v-img>
                                            <div
                                                v-if="photo.nc_watermarked === false"
                                                class="nc-photo-preview__warning"
                                            >
                                                PEZZO NON CONFORME · DA MARCARE
                                            </div>
                                        </div>
                                        <mc-status-chip
                                            v-if="photo.nc_watermarked !== null"
                                            :tone="photo.nc_watermarked ? 'good' : 'bad'"
                                            :icon="photo.nc_watermarked ? 'check' : 'alert'"
                                            :label="photo.nc_watermarked ? 'Dicitura impressa nella foto' : 'Foto storica non ancora marcata'"
                                            class="mt-1"
                                        />
                                        <v-checkbox v-model="images_delete" :value="photo.code" label="Elimina" dense hide-details color="primary"></v-checkbox>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-file-input
                                            outlined dense hide-details="auto"
                                            v-model="imageGroups.defect"
                                            prepend-icon="mdi-camera"
                                            counter
                                            show-size
                                            label="Foto del difetto"
                                            multiple
                                        ></v-file-input>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-file-input
                                            outlined dense hide-details="auto"
                                            v-model="imageGroups.reference"
                                            prepend-icon="mdi-file-document-outline"
                                            counter
                                            show-size
                                            label="Documento/disegno di riferimento"
                                            multiple
                                        ></v-file-input>
                                    </v-col>
                                </v-row>
                                <v-alert dense text type="info" class="mb-0">
                                    Ogni nuova fotografia viene archiviata con la dicitura permanente
                                    <strong>“PEZZO NON CONFORME”</strong>. L’originale resta conservato per la tracciabilità.
                                </v-alert></template>
                                <slot v-else name="evidence" />
                                <template v-if="internalCaseId && nc.verifica_efficacia">
                                    <h3 class="t-title-3 mt-4">Verifica dell’efficacia</h3>
                                    <v-textarea v-model="nc.verifica_efficacia.criterio" label="Criterio di verifica" outlined dense auto-grow rows="2" />
                                    <v-select v-model="nc.verifica_efficacia.stato" :items="['In attesa di verifica','Efficace','Non efficace']" label="Esito della verifica" outlined dense />
                                    <v-text-field v-model="nc.verifica_efficacia.data_verifica" label="Data verifica" type="date" outlined dense />
                                    <v-text-field v-model="nc.verifica_efficacia.riferimento" label="Lotto, ODP o riferimento verificato" outlined dense />
                                    <v-textarea v-model="nc.verifica_efficacia.esito" label="Risultato della verifica" outlined dense auto-grow rows="2" />
                                </template>
                            </v-card-text>
                        </v-card>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions class="nc-actions">
                    <template v-if="canGenerate8d && !editingCompleted">
                        <v-btn text @click="dialog = false">Chiudi</v-btn>
                        <v-btn depressed color="primary" :loading="generating" @click="generate8d">Apri 8D</v-btn>
                        <v-btn outlined @click="editConfirmDialog = true">
                            Modifica NC
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn text @click="editingCompleted ? cancelEdit() : (dialog = false)">Annulla</v-btn>
                        <v-btn outlined :loading="draftSaving" @click="save('save')">Salva bozza</v-btn>
                        <v-btn depressed color="primary" :loading="completing" @click="completeDeclaration">
                            {{ editingCompleted ? "Completa e rigenera 8D" : "Completa dichiarazione" }}
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="editConfirmDialog" max-width="560">
            <v-card class="mc-root mc-dialog nc-mecom nc-confirm">
                <v-card-title>Modificare una NC completata?</v-card-title>
                <v-card-text>
                    Il rapporto 8D attuale resta consultabile finché non si salva la modifica.
                    Dopo il salvataggio, la dichiarazione dovrà essere nuovamente completata e il rapporto verrà rigenerato.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="editConfirmDialog = false">Annulla</v-btn>
                    <v-btn depressed color="primary" @click="startCompletedEdit">Modifica NC</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="copyConfirmDialog" max-width="620">
            <v-card class="mc-root mc-dialog nc-mecom nc-confirm">
                <v-card-title>Conferma copia testi</v-card-title>
                <v-card-text>
                    I testi saranno copiati soltanto nelle righe incomplete della stessa NC.
                    Articolo, quantità, ODP, fase e operatori non verranno modificati.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="copyConfirmDialog = false">Annulla</v-btn>
                    <v-btn
                        color="primary"
                        depressed
                        :loading="copying"
                        @click="
                            copyConfirmDialog = false;
                            copyTexts();
                        "
                    >
                        Conferma copia
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="photoDialog" max-width="90vw">
            <v-card v-if="selectedPhoto" class="mc-root mc-dialog nc-mecom nc-photo-dialog">
                <v-card-title>{{ selectedPhoto.name }}</v-card-title>
                <v-card-text>
                    <v-alert v-if="selectedPhoto.nc_watermarked === false" dense type="error">
                        PEZZO NON CONFORME · fotografia storica in attesa di marcatura permanente
                    </v-alert>
                    <v-img :src="fullurl + selectedPhoto.code + '/'" contain height="80vh"></v-img>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {
    CORRECTION_SUGGESTIONS,
    PROBLEM_SUGGESTIONS,
    configureNcSuggestions,
    causeOptionsForOrigin,
    groupedNcOriginChoices,
    ncCustomerLabel,
    ncDecisionChecklist,
    ncLiveChecklist,
    ncGenerationErrors,
    ncNonDetectionCause,
    ncOperatorsForPhase,
    ncOriginSelection,
    ncRequiresInternalResponsible,
    ncResponsibleOptions,
    permanentSuggestionsForOrigin,
    resolveNcOriginChoice,
} from "./ncOrigin.mjs";
import { bus } from "@/main";
import NcGuidedTextarea from "./NcGuidedTextarea.vue";
import { ncDeclarationPaths } from "./ncDeclarationApi.mjs";
import McStatusChip from "@/assets/mecom/vue/McStatusChip.vue";

let ncRelatedCatalogPromise = null;

function getNcRelatedCatalog(workApi) {
    if (!ncRelatedCatalogPromise) {
        // Un solo catalogo condiviso: la tabella pol montar decine de editor insieme.
        ncRelatedCatalogPromise = workApi
            .get("/arts/nc/", { related: true })
            .then((response) => response.data)
            .catch((error) => {
                // Un errore temporaneo no ga da avvelenar i tentativi successivi.
                ncRelatedCatalogPromise = null;
                throw error;
            });
    }
    return ncRelatedCatalogPromise;
}

export default {
    name: "NcEdit",
    components: { NcGuidedTextarea, McStatusChip },
    props: {
        input: { type: Object, required: true },
        articolo: { type: String, required: true },
        getdata: { type: Function, required: true },
        buttonLabel: { type: String, default: "" },
        buttonColor: { type: String, default: "info" },
        disabled: { type: Boolean, default: false },
        openOnMount: { type: Boolean, default: false },
        internalCaseId: { type: String, default: "" },
        internalCanWrite: { type: Boolean, default: false },
    },
    data() {
        return {
            dialog: false,
            photoDialog: false,
            copyConfirmDialog: false,
            editConfirmDialog: false,
            editingCompleted: false,
            regenerateAfterComplete: false,
            selectedPhoto: null,
            imageGroups: {
                defect: [],
                reference: [],
            },
            images_delete: [],
            detailLoading: false,
            draftSaving: false,
            completing: false,
            generating: false,
            copying: false,
            materialLoading: false,
            originLoading: false,
            risolta: false,
            nc: null,
            cause: [],
            odpOptions: [],
            phaseOptions: [],
            nonProductiveOrigins: [],
            internalOperators: [],
            selectedOdp: null,
            selectedPhase: null,
            selectedOriginChoice: null,
            selectedOperator: null,
            selectedSupplier: null,
            generationErrors: [],
            validationRequested: false,
            persistedDeclarationFingerprint: "",
            savedDeclarationComplete: false,
            problemSuggestions: PROBLEM_SUGGESTIONS,
            correctionSuggestions: CORRECTION_SUGGESTIONS,
            // eslint-disable-next-line no-undef
            thumburl: window.location.protocol + "//" + process.env.VUE_APP_WORK_URL + "/commesse/thumbnail/",
            // eslint-disable-next-line no-undef
            fullurl: window.location.protocol + "//" + process.env.VUE_APP_WORK_URL + "/commesse/showfile/",
        };
    },
    mounted() {
        if (this.openOnMount && this.canManageNc) this.dialog = true;
    },
    computed: {
        declarationPaths() { return ncDeclarationPaths(this.nc || this.input, this.articolo, this.internalCaseId); },
        canManageNc() {
            if (this.internalCaseId) return this.internalCanWrite;
            // El tasto segue i stessi gruppi autorizzati dal backend NC.
            const groups = this.$store.state.user.userdata.groups || [];
            return ["Amministrazione", "Qualità", "Milkrun", "Tecnici", "Magazzino"].some((group) =>
                groups.includes(group)
            );
        },
        originChoiceOptions() {
            return groupedNcOriginChoices(this.phaseOptions, this.nonProductiveOrigins);
        },
        calculatedOriginLabel() {
            return resolveNcOriginChoice(
                this.selectedOriginChoice,
                this.originChoiceOptions
            ).originLabel;
        },
        filteredCauseOptions() {
            return causeOptionsForOrigin(this.cause, this.nc && this.nc.nc_origine_id);
        },
        nonDetectionCause() {
            return ncNonDetectionCause(this.selectedPhase, this.phaseOptions);
        },
        operatorsForSelectedPhase() {
            const fallback = this.nc && Array.isArray(this.nc.operatori) ? this.nc.operatori : [];
            return ncOperatorsForPhase(this.phaseOptions, this.selectedPhase, fallback);
        },
        responsibleErrorRequired() {
            return ncRequiresInternalResponsible(
                this.nc && this.nc.nc_origine_id,
                this.selectedPhase
            );
        },
        internalNonProductiveOrigin() {
            return this.responsibleErrorRequired && Number(this.selectedPhase) === 999;
        },
        responsibleOptions() {
            const fallback = this.nc && Array.isArray(this.nc.operatori) ? this.nc.operatori : [];
            return ncResponsibleOptions(
                this.phaseOptions,
                this.selectedPhase,
                fallback,
                this.internalOperators
            );
        },
        responsibleErrorHint() {
            return this.internalNonProductiveOrigin
                ? "Cerca fra dipendenti e operatori attivi per matricola, nome o cognome; obbligatorio solo per completare"
                : "Operatore registrato sulla fase; obbligatorio solo per completare";
        },
        responsibleErrorNoDataText() {
            return this.internalNonProductiveOrigin
                ? "Nessun dipendente o operatore attivo trovato"
                : "Nessun operatore registrato per la fase";
        },
        supplierOptions() {
            const phase = this.phaseOptions.find(
                (item) => Number(item.numero) === Number(this.selectedPhase)
            );
            return phase && Array.isArray(phase.fornitori) ? phase.fornitori : [];
        },
        operatorRules() {
            return [
                (value) => !this.validationRequested
                    || Boolean(value)
                    || "Selezionare il responsabile dell’errore",
            ];
        },
        liveChecklist() {
            const selectedOption = this.odpOptions.find(
                (item) => item.numero === this.selectedOdp
            );
            const validOdp = Boolean(
                selectedOption
                && (this.internalCaseId ? selectedOption.data_riferimento_odp : selectedOption.data_versamento_odp)
                && !selectedOption.disabled
            );
            const detail = {
                ...(this.nc || {}),
                fornitore_origine: this.selectedSupplier
                    ? { codice: this.selectedSupplier }
                    : null,
            };
            const checklist = ncLiveChecklist(
                detail,
                this.selectedOdp,
                this.selectedPhase,
                validOdp
            );
            if (this.responsibleErrorRequired) {
                checklist.push({
                    key: "operator",
                    label: "Operatore origine selezionato",
                    complete: Boolean(this.selectedOperator),
                });
            }
            return checklist;
        },
        isDeclarationComplete() {
            return this.liveChecklist.every((item) => item.complete);
        },
        decisionChecklist() {
            return ncDecisionChecklist(this.nc || {}, this.liveChecklist);
        },
        currentDeclarationFingerprint() {
            return JSON.stringify({
                odp: this.selectedOdp,
                phase: this.selectedPhase,
                operator: this.selectedOperator,
                supplier: this.selectedSupplier,
                origin: this.nc && this.nc.nc_origine_id,
                cause: this.nc && this.nc.nc_causa_id,
                problem: this.nc && this.nc.problema,
                correction: this.nc && this.nc.correzione_immediata,
                permanent: this.nc && this.nc.azione_correttiva_permanente,
                noAction: this.nc && Boolean(this.nc.azione_non_necessaria),
                motivation: this.nc && this.nc.motivazione_azione_non_necessaria,
                effectiveness: this.internalCaseId && this.nc ? this.nc.verifica_efficacia : null,
            });
        },
        canGenerate8d() {
            return Boolean(
                this.savedDeclarationComplete
                && this.isDeclarationComplete
                && this.persistedDeclarationFingerprint
                && this.persistedDeclarationFingerprint === this.currentDeclarationFingerprint
            );
        },
        generationErrorList() {
            if (this.generationErrors.length) return this.generationErrors;
            if (!this.validationRequested) return [];
            const selectedOption = this.odpOptions.find(
                (item) => item.numero === this.selectedOdp
            );
            const validOdp = Boolean(
                selectedOption
                && (this.internalCaseId ? selectedOption.data_riferimento_odp : selectedOption.data_versamento_odp)
                && !selectedOption.disabled
            );
            const errors = ncGenerationErrors(
                {
                    ...(this.nc || {}),
                    fornitore_origine: this.selectedSupplier
                        ? { codice: this.selectedSupplier }
                        : null,
                },
                this.selectedOdp,
                this.selectedPhase,
                validOdp
            );
            if (this.responsibleErrorRequired && !this.selectedOperator) {
                errors.push("responsabile dell’errore non selezionato");
            }
            return errors;
        },
        declarationState() {
            if (this.canGenerate8d) return "Completa";
            const started = this.nc && [
                this.nc.problema,
                this.nc.correzione_immediata,
                this.nc.azione_correttiva_permanente,
                this.nc.motivazione_azione_non_necessaria,
            ].some((value) => String(value || "").trim());
            return started ? "In compilazione" : "Non iniziata";
        },
        materialState() {
            return this.risolta ? "Materiale gestito" : "Materiale da gestire";
        },
        permanentSuggestions() {
            return permanentSuggestionsForOrigin(
                this.nc && this.nc.nc_origine_id,
                this.nc && this.nc.nc_causa_id
            );
        },
        ncDataFields() {
            if (!this.nc) return [];
            const fields = [
                { label: "NC", value: this.nc.numero },
                { label: "Riga", value: this.nc.riga },
                { label: "Data rilevazione", value: this.formatNcDate(this.nc.data_nc) },
                { label: "Cliente", value: ncCustomerLabel(this.nc) },
                { label: "Sede di rilevazione", value: this.nc.sede_cliente },
                { label: "Riferimento cliente", value: this.nc.riferimento_cliente },
                { label: "Articolo", value: this.nc.articolo },
                { label: "Descrizione", value: this.nc.descrizione_articolo },
                {
                    label: "Quantità non conforme",
                    value: `${this.nc.quantita_non_conforme || ""} ${this.nc.unita_misura || ""}`.trim(),
                },
                {
                    label: "Gestione prevista dal reso",
                    value: this.nc.gestione,
                },
                {
                    label: "Responsabile NC",
                    value: this.nc.responsabile_nc && this.nc.responsabile_nc.nome_completo,
                },
            ];
            if (this.internalCaseId) {
                return fields.filter(field => !["Riga", "Cliente", "Sede di rilevazione", "Riferimento cliente"].includes(field.label))
                    .map(field => field.label === "Gestione prevista dal reso" ? {...field,label:"Gestione"} : field)
                    .concat([{label:"Sede di rilevazione",value:this.nc.sede_interna},{label:"Data evento",value:this.formatNcDate(this.nc.data_evento)}]);
            }
            return fields;
        },
    },
    watch: {
        dialog(value) {
            this.$emit("dialog-change", value);
            if (value) this.openDialog();
        },
        currentDeclarationFingerprint() {
            if (!this.validationRequested) return;
            const missingKeys = new Set(
                this.liveChecklist
                    .filter((item) => !item.complete)
                    .map((item) => item.key)
            );
            // Gli errori strutturati resta finché el relativo campo manca;
            // i vecchi testi liberi vien ricalcolai dal gate locale.
            this.generationErrors = this.generationErrors.filter((error) => (
                error
                && typeof error === "object"
                && missingKeys.has(error.key)
            ));
        },
    },
    methods: {
        async openDialog() {
            // Clonemo la riga: Annulla no ga da modificar la tabella sottostante.
            this.nc = JSON.parse(JSON.stringify(this.input));
            this.images_delete = [];
            this.generationErrors = [];
            this.validationRequested = false;
            this.editConfirmDialog = false;
            this.editingCompleted = false;
            this.regenerateAfterComplete = false;
            this.persistedDeclarationFingerprint = "";
            this.savedDeclarationComplete = false;
            this.imageGroups = {
                defect: [],
                reference: [],
            };
            this.selectedOdp = null;
            this.selectedPhase = null;
            this.selectedOriginChoice = null;
            this.selectedOperator = null;
            this.selectedSupplier = null;
            this.phaseOptions = [];
            this.nonProductiveOrigins = [];
            this.internalOperators = [];
            await Promise.all([this.loadNcDetail(), this.loadRelated()]);
        },
        async loadNcDetail() {
            this.detailLoading = true;
            try {
                const response = await this.$work.get(
                    this.declarationPaths.detail,
                    {},
                    false,
                    null,
                    { timeout: 15000 }
                );
                this.nc = response.data;
                this.risolta = this.nc.risolta === 2;
                if (!this.nc.correzione_immediata && this.nc.correzione_precompilata) {
                    this.$set(this.nc, "correzione_immediata", this.nc.correzione_precompilata);
                }
                this.selectedOdp = this.nc.odp || null;
                await this.loadOriginOptions(this.selectedOdp);
                this.selectedOriginChoice = ncOriginSelection(this.nc);
                this.applyOriginChoice(this.selectedOriginChoice);
                const savedOperator = this.nc.operatore_origine_matricola
                    ? String(this.nc.operatore_origine_matricola)
                    : null;
                this.selectedOperator = this.responsibleErrorRequired
                    ? savedOperator
                    : null;
                this.selectedSupplier = this.nc.fornitore_origine
                    ? this.nc.fornitore_origine.codice
                    : null;
                this.savedDeclarationComplete = this.nc.stato_dichiarazione === "Completa";
                this.persistedDeclarationFingerprint = this.currentDeclarationFingerprint;
            } catch (error) {
                this.$toast.error("Impossibile caricare il dettaglio della NC", {
                    icon: "mdi-alert",
                    timeout: 5000,
                });
            } finally {
                this.detailLoading = false;
            }
        },
        async loadOriginOptions(odp = null) {
            this.originLoading = true;
            try {
                const params = {
                    origin: true,
                    numero: this.nc.numero,
                    riga: this.nc.riga,
                    articolo: this.articolo,
                };
                if (odp) params.odp = odp;
                const response = await this.$work.get(
                    this.declarationPaths.origin,
                    params,
                    false,
                    null,
                    { timeout: 15000 }
                );
                this.odpOptions = response.data.odps || [];
                this.phaseOptions = response.data.fasi || [];
                this.nonProductiveOrigins = response.data.origini_non_produttive || [];
                this.internalOperators = response.data.operatori_interni || [];
                if (response.data.warning) {
                    this.$toast.warning(response.data.warning, {
                        icon: "mdi-alert-circle",
                        timeout: 7000,
                    });
                }
                if (odp && !this.odpOptions.some((item) => item.numero === odp)) {
                    this.odpOptions.unshift({
                        numero: odp,
                        label: `${odp} - storico non valido per il completamento`,
                        disabled: true,
                    });
                }
                if (!odp && !this.selectedOdp && this.odpOptions.length === 1) {
                    this.selectedOdp = this.odpOptions[0].numero;
                    await this.loadOriginOptions(this.selectedOdp);
                    return;
                }
            } catch (error) {
                this.phaseOptions = [];
                this.nonProductiveOrigins = [];
                this.internalOperators = [];
                this.$toast.error("Impossibile caricare ODP e fasi compatibili", {
                    icon: "mdi-alert",
                    timeout: 5000,
                });
            } finally {
                this.originLoading = false;
            }
        },
        async onOdpChange(odp) {
            this.selectedPhase = null;
            this.selectedOriginChoice = null;
            this.selectedOperator = null;
            this.selectedSupplier = null;
            if (this.nc) this.$set(this.nc, "nc_origine_id", null);
            if (this.nc) this.$set(this.nc, "operatori", []);
            if (!odp) {
                this.phaseOptions = [];
                this.nonProductiveOrigins = [];
                return;
            }
            await this.loadOriginOptions(odp);
        },
        applyOriginChoice(choice) {
            const selected = resolveNcOriginChoice(choice, this.originChoiceOptions);
            this.selectedPhase = selected.phase;
            if (this.nc) {
                const previousOrigin = this.nc.nc_origine_id;
                this.$set(this.nc, "nc_origine_id", selected.originId);
                this.$set(this.nc, "origine", selected.originLabel);
                if (previousOrigin !== selected.originId) {
                    this.$set(this.nc, "nc_causa_id", null);
                }
                if (Number(selected.phase) === 999) this.$set(this.nc, "operatori", []);
                if (selected.originId !== "O17") {
                    this.selectedSupplier = null;
                    this.$set(this.nc, "fornitore_origine", null);
                } else if (this.supplierOptions.length === 1) {
                    this.selectedSupplier = this.supplierOptions[0].codice;
                }
            }
        },
        onOriginChoiceChange(choice) {
            const previousOperator = this.selectedOperator;
            this.applyOriginChoice(choice);
            const stillAvailable = this.responsibleOptions.some(
                (operator) => this.operatorValue(operator) === String(previousOperator || "")
            );
            this.selectedOperator = stillAvailable ? previousOperator : null;
            if (this.nc.nc_origine_id !== "O17") this.selectedSupplier = null;
        },
        operatorLabel(operator) {
            return `${operator.matricola} – ${operator.nome_completo || "Nome non disponibile"}`;
        },
        operatorValue(operator) {
            return String(operator.matricola);
        },
        operatorFilter(item, queryText, itemText) {
            const haystack = `${itemText || ""} ${item && item.matricola || ""} ${item && item.nome_completo || ""}`
                .toLocaleLowerCase("it");
            return haystack.includes(String(queryText || "").trim().toLocaleLowerCase("it"));
        },
        operatorSubtitle(operator) {
            if (this.internalNonProductiveOrigin) {
                return "Dipendente / operatore Sage attivo";
            }
            const date = this.formatNcDate(operator.data_ultimo_avanzamento);
            const timestamp = [date, operator.ora_ultimo_avanzamento].filter(Boolean).join(" ");
            return `Ultimo avanzamento fase: ${timestamp || "non disponibile"}`;
        },
        formatNcDate(value) {
            const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
            return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
        },
        async loadRelated() {
            const cachedCauses = await this.$vlf.getItem("nc_cause_wk1234");
            if (cachedCauses) this.cause = cachedCauses;
            try {
                const related = await getNcRelatedCatalog(this.$work);
                this.cause = related.cause || [];
                const suggestions = related.suggerimenti || {};
                configureNcSuggestions(suggestions);
                if (Array.isArray(suggestions.problemi)) {
                    this.problemSuggestions = suggestions.problemi;
                }
                if (Array.isArray(suggestions.correzioni)) {
                    this.correctionSuggestions = suggestions.correzioni;
                }
                await this.$vlf.setItem("nc_cause_wk1234", this.cause);
            } catch (error) {
                if (!cachedCauses) {
                    this.$toast.error("Impossibile caricare la categoria causa", {
                        icon: "mdi-alert",
                        timeout: 5000,
                    });
                }
            }
        },
        buildFormData(action) {
            const formData = new FormData();
            formData.append("action", action);
            formData.append("numero", this.nc.numero);
            formData.append("riga", this.nc.riga);
            formData.append("odp", this.selectedOdp || "");
            formData.append("fase_numero", this.selectedPhase === null ? "" : this.selectedPhase);
            if (this.responsibleErrorRequired) {
                formData.append("operatore_matricola", this.selectedOperator || "");
            }
            formData.append("fornitore_origine_codice", this.selectedSupplier || "");
            formData.append("nc_causa_id", this.nc.nc_causa_id || "");
            formData.append("nc_origine_id", this.nc.nc_origine_id || "");
            formData.append("problema", this.nc.problema || "");
            formData.append("correzione_immediata", this.nc.correzione_immediata || "");
            formData.append(
                "azione_correttiva_permanente",
                this.nc.azione_correttiva_permanente || ""
            );
            formData.append("azione_non_necessaria", Boolean(this.nc.azione_non_necessaria));
            formData.append(
                "motivazione_azione_non_necessaria",
                this.nc.motivazione_azione_non_necessaria || ""
            );
            formData.append("images_delete", this.images_delete);
            const images = Object.values(this.imageGroups).flat();
            images.forEach((file, index) => {
                formData.append(`file_${index + 1}`, file, file.name);
            });
            return formData;
        },
        declarationPayload(action) {
            const form = this.buildFormData(action);
            if (!this.internalCaseId) return form;
            const payload = Object.fromEntries(form.entries());
            payload.action = action === "save" ? "draft" : action;
            for (const key of ["numero", "riga", "images_delete"]) delete payload[key];
            payload.revision = this.nc.declaration_revision;
            payload.fase_numero = this.selectedPhase == null ? null : Number(this.selectedPhase);
            payload.azione_non_necessaria = Boolean(this.nc.azione_non_necessaria);
            payload.verifica_efficacia = {...this.nc.verifica_efficacia,data_verifica:this.nc.verifica_efficacia.data_verifica || null};
            return payload;
        },
        async save(action) {
            const stateKey = action === "complete" ? "completing" : "draftSaving";
            if (this[stateKey]) return;
            if (action === "complete") {
                await this.$nextTick();
                this.validationRequested = true;
                this.generationErrors = [];
                if (!this.isDeclarationComplete) {
                    await this.focusFirstMissing();
                    return;
                }
            }
            const body = this.$el.querySelector(".nc-body");
            const scrollTop = body ? body.scrollTop : 0;
            this[stateKey] = true;
            try {
                await this.$work.post(
                    this.declarationPaths.save,
                    this.declarationPayload(action),
                    true,
                    this.internalCaseId ? {} : { "Content-Type": "multipart/form-data" },
                    { timeout: 15000 }
                );
                // La bozza ga finìo de salvar quando termina il POST: la
                // rilettura successiva no deve tenere il bottone in loading.
                if (action !== "complete") this[stateKey] = false;
                this.$toast.success(
                    action === "complete" ? "Dichiarazione completata" : "Bozza salvata",
                    { icon: "mdi-check-circle", timeout: 3000 }
                );
                await this.loadNcDetail();
                this.$emit("saved", this.nc);
                if (action === "complete" && this.regenerateAfterComplete) {
                    this.regenerateAfterComplete = false;
                    await this.generate8d();
                }
                await this.$nextTick();
                const refreshedBody = this.$el.querySelector(".nc-body");
                if (refreshedBody) refreshedBody.scrollTop = scrollTop;
            } catch (error) {
                const payload = error.response && error.response.data;
                const detail = payload && payload.detail;
                this.generationErrors = payload && Array.isArray(payload.missing_fields)
                    ? payload.missing_fields
                    : [];
                if (this.generationErrors.length) {
                    this.validationRequested = true;
                    await this.focusFirstMissing();
                }
                this.$toast.error(detail || "Impossibile salvare la dichiarazione", {
                    icon: "mdi-alert",
                    timeout: 6000,
                });
            } finally {
                this[stateKey] = false;
            }
        },
        startCompletedEdit() {
            this.editConfirmDialog = false;
            this.editingCompleted = true;
            this.validationRequested = false;
            this.generationErrors = [];
        },
        completeDeclaration() {
            this.regenerateAfterComplete = this.editingCompleted;
            return this.save("complete");
        },
        async cancelEdit() {
            await this.loadNcDetail();
            this.editingCompleted = false;
            this.regenerateAfterComplete = false;
            this.validationRequested = false;
            this.generationErrors = [];
        },
        fieldErrors(key) {
            if (!this.validationRequested) return [];
            const missing = this.liveChecklist.find((item) => item.key === key && !item.complete);
            if (!missing) return [];
            const messages = {
                problem: "Selezionare un problema preimpostato oppure descrivere il problema.",
                correction: "Descrivere la correzione eseguita sul materiale con almeno 30 caratteri; indicare cosa è stato corretto e l’esito sul lotto contestato.",
                supplier: "Selezionare il fornitore o terzista collegato all’ODP e alla fase.",
                cause: "Selezionare la categoria della causa.",
                permanent_action: this.nc.azione_non_necessaria
                    ? "Inserire una motivazione valida."
                    : "Selezionare o descrivere un’azione correttiva permanente valida.",
            };
            return [messages[key] || missing.label];
        },
        async focusFirstMissing() {
            await this.$nextTick();
            const order = ["odp", "phase", "supplier", "operator", "cause", "problem", "correction", "permanent_action"];
            const missingKeys = new Set(
                this.liveChecklist.filter((item) => !item.complete).map((item) => item.key)
            );
            const targetKey = order.find((key) => missingKeys.has(key));
            const target = targetKey && this.$refs[targetKey];
            if (!target) return;
            if (target.$el && typeof target.$el.scrollIntoView === "function") {
                target.$el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            if (typeof target.focus === "function") target.focus();
        },
        supplierLabel(supplier) {
            return `${supplier.codice} – ${supplier.ragione_sociale}`;
        },
        async generate8d() {
            if (this.generating) return;
            this.validationRequested = true;
            if (!this.canGenerate8d) return;
            this.generating = true;
            this.generationErrors = [];
            try {
                const response = await this.$work.get(
                    this.declarationPaths.pdf,
                    {},
                    false,
                    null,
                    {
                        responseType: "blob",
                        timeout: 15000,
                        ignoreSentryStatuses: [400, 403, 404],
                        // El dialogo ga già un errore contestuale: no mostremo anca
                        // l'"Errore Connessione" generico per i fallimenti senza status.
                        ignoreGlobalErrorStatuses: [undefined, 400, 403, 404, 503],
                    }
                );
                const contentType = String(response.headers && response.headers["content-type"] || "").toLowerCase();
                const pdfBlob = response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], { type: contentType });
                const signature = await pdfBlob.slice(0, 5).text();
                if (
                    response.status !== 200
                    || !contentType.includes("application/pdf")
                    || !signature.startsWith("%PDF")
                ) {
                    throw new Error("Risposta 8D non valida");
                }
                const pdfUrl = URL.createObjectURL(pdfBlob);
                // El viewer globale gestisse apertura e revoke quando vien chiuso.
                try {
                    bus.$emit("show-pdf", {
                        disegno: `Rapporto 8D ${this.nc.numero} / riga ${this.nc.riga}`,
                        minio: pdfUrl,
                        revokeOnClose: true,
                    });
                } catch (error) {
                    URL.revokeObjectURL(pdfUrl);
                    throw error;
                }
            } catch (error) {
                let payload = error.response && error.response.data;
                if (payload && typeof payload.text === "function") {
                    try {
                        payload = JSON.parse(await payload.text());
                    } catch {
                        payload = null;
                    }
                }
                const detail = payload && payload.detail;
                this.generationErrors = (
                    payload
                    && Array.isArray(payload.missing_fields)
                    && payload.missing_fields.length
                )
                    ? payload.missing_fields
                    : String(detail || "")
                        .split("\n")
                        .filter((line) => line.startsWith("- "))
                        .map((line) => line.slice(2));
                const incomplete = error.response && error.response.status === 400;
                this.$toast.error(
                    incomplete
                        ? "Il rapporto 8D non può essere generato perché la dichiarazione è incompleta."
                        : "Non è stato possibile generare il rapporto 8D. Riprovare più tardi.",
                    {
                    icon: "mdi-alert",
                    timeout: 6000,
                    }
                );
            } finally {
                this.generating = false;
            }
        },
        async updateMaterial(value) {
            const previous = !value;
            this.materialLoading = true;
            const formData = new FormData();
            formData.append("action", "material");
            formData.append("numero", this.nc.numero);
            formData.append("riga", this.nc.riga);
            formData.append("risolta", value);
            try {
                const response = await this.$work.post(this.declarationPaths.save, this.internalCaseId ? {action:"material",revision:this.nc.declaration_revision,risolta:value} : formData);
                if (this.internalCaseId) this.$set(this.nc, "declaration_revision", response.data.declaration_revision);
                this.$set(this.nc, "risolta", value ? 2 : 1);
                this.$toast.success("Stato materiale aggiornato", {
                    icon: "mdi-check-circle",
                    timeout: 3000,
                });
            } catch (error) {
                this.risolta = previous;
                this.$toast.error("Impossibile aggiornare lo stato materiale", {
                    icon: "mdi-alert",
                    timeout: 5000,
                });
            } finally {
                this.materialLoading = false;
            }
        },
        async copyTexts() {
            this.copying = true;
            const formData = new FormData();
            formData.append("action", "copy_texts");
            formData.append("numero", this.nc.numero);
            formData.append("riga", this.nc.riga);
            try {
                const response = await this.$work.post(`/arts/nc/${this.articolo}/`, formData);
                this.$toast.success(
                    `Testi copiati su ${response.data.righe_aggiornate} righe incomplete`,
                    { icon: "mdi-content-copy", timeout: 4000 }
                );
            } catch (error) {
                this.$toast.error("Impossibile copiare i testi", {
                    icon: "mdi-alert",
                    timeout: 5000,
                });
            } finally {
                this.copying = false;
            }
        },
        openPhoto(photo) {
            this.selectedPhoto = photo;
            this.photoDialog = true;
        },
    },
};
</script>

<style src="./ncDialog.css"></style>
